import React, { useState, useEffect } from 'react';
import { useAuth } from '../context/AuthContext';
import { authService } from '../api/services/authService';
import '../styles/UserManagement.css';

function UserManagement() {
  const { user } = useAuth();
  const [users, setUsers] = useState([]);
  const [showModal, setShowModal] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [formData, setFormData] = useState({
    username: '',
    password: '',
    fullName: '',
    role: 'Waff Clerk',
    email: ''
  });
  const [message, setMessage] = useState('');
  const [searchTerm, setSearchTerm] = useState('');

  useEffect(() => {
    if (user?.role === 'Super Admin') {
      fetchUsers();
    }
  }, [user]);

  const fetchUsers = async () => {
    try {
      const data = await authService.getUsers();
      setUsers(data);
    } catch (error) {
      console.error('Error fetching users:', error);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      await authService.createUser(formData);
      setMessage('User created successfully!');
      setFormData({ username: '', password: '', fullName: '', role: 'Waff Clerk', email: '' });
      setShowPassword(false);
      setShowModal(false);
      fetchUsers();
      setTimeout(() => setMessage(''), 3000);
    } catch (error) {
      setMessage(error.response?.data?.message || 'Error creating user');
      setTimeout(() => setMessage(''), 5000);
    }
  };

  const validateUsernameInput = (value) => {
    // Allow letters, underscores, and hyphens only (no numbers)
    return /^[a-zA-Z_-]*$/.test(value);
  };

  const validateNameInput = (value) => {
    // Allow letters, spaces, and hyphens only (no numbers)
    return /^[a-zA-Z\s-]*$/.test(value);
  };

  const handleChange = (e) => {
    let { name, value } = e.target;

    // Apply validation for username and fullName
    if (name === 'username' && !validateUsernameInput(value)) {
      return; // Don't update if invalid
    }
    if (name === 'fullName' && !validateNameInput(value)) {
      return; // Don't update if invalid
    }

    setFormData({ ...formData, [name]: value });
  };

  const handleUsernameKeyDown = (e) => {
    // Allow: letters, underscore, hyphen, backspace, delete, arrow keys, tab
    const allowedKeys = ['Backspace', 'Delete', 'ArrowLeft', 'ArrowRight', 'Tab'];
    const char = String.fromCharCode(e.which || e.keyCode);

    if (!allowedKeys.includes(e.key) && !/^[a-zA-Z_-]$/.test(char)) {
      e.preventDefault();
    }
  };

  const handleNameKeyDown = (e) => {
    // Allow: letters, space, hyphen, backspace, delete, arrow keys, tab
    const allowedKeys = ['Backspace', 'Delete', 'ArrowLeft', 'ArrowRight', 'Tab'];
    const char = String.fromCharCode(e.which || e.keyCode);

    if (!allowedKeys.includes(e.key) && !/^[a-zA-Z\s-]$/.test(char)) {
      e.preventDefault();
    }
  };

  const handleUsernamePaste = (e) => {
    const pastedText = e.clipboardData.getData('text');
    if (!validateUsernameInput(pastedText)) {
      e.preventDefault();
    }
  };

  const handleNamePaste = (e) => {
    const pastedText = e.clipboardData.getData('text');
    if (!validateNameInput(pastedText)) {
      e.preventDefault();
    }
  };

  const filteredUsers = users.filter(u =>
    (u.username || '').toLowerCase().includes(searchTerm.toLowerCase()) ||
    (u.fullName || '').toLowerCase().includes(searchTerm.toLowerCase()) ||
    (u.email || '').toLowerCase().includes(searchTerm.toLowerCase())
  );

  if (user?.role !== 'Super Admin') {
    return (
      <div className="container">
        <div className="alert alert-error">Access Denied: Super Admin only</div>
      </div>
    );
  }

  return (
    <div className="container users-page">
      <div className="page-header">
        <div>
          <h1>User Management</h1>
          <p>Manage system users and their roles</p>
        </div>
        <button onClick={() => setShowModal(true)} className="btn btn-primary">
          + New User
        </button>
      </div>

      {message && (
        <div className={`alert ${message.includes('Error') ? 'alert-error' : 'alert-success'}`}>
          {message}
        </div>
      )}

      <div className="card">
        <div className="card-header">
          <h2>All Users ({filteredUsers.length})</h2>
          <div className="search-box">
            <input
              type="text"
              placeholder="Search by username, name, or email..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="search-input"
            />
          </div>
        </div>

        {filteredUsers.length === 0 ? (
          <div className="empty-state">
            <div className="empty-state-icon">
              <svg width="80" height="80" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
                <path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2"></path>
                <circle cx="12" cy="7" r="4"></circle>
              </svg>
            </div>
            <p>{searchTerm ? 'No users found matching your search' : 'No users found'}</p>
          </div>
        ) : (
          <div className="users-table-wrapper">
            <table className="users-table">
              <thead>
                <tr>
                  <th>User ID</th>
                  <th>Username</th>
                  <th>Full Name</th>
                  <th>Email</th>
                  <th>Role</th>
                  <th>Created Date</th>
                </tr>
              </thead>
              <tbody>
                {filteredUsers.map(u => (
                  <tr key={u.userId}>
                    <td data-label="User ID"><strong className="user-id">{u.userId}</strong></td>
                    <td data-label="Username"><strong>{u.username}</strong></td>
                    <td data-label="Full Name">{u.fullName}</td>
                    <td data-label="Email" className="email-cell">{u.email}</td>
                    <td data-label="Role">
                      <span className={`role-badge role-${u.role.toLowerCase().replace(' ', '-')}`}>
                        {u.role}
                      </span>
                    </td>
                    <td data-label="Created Date">{new Date(u.createdDate).toLocaleDateString()}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {showModal && (
        <div className="modal-overlay">
          <div className="modal modal-large">
            <div className="modal-header">
              <h2>Create New User</h2>
              <button className="btn-close" onClick={() => setShowModal(false)}>×</button>
            </div>
            <form onSubmit={handleSubmit} className="user-form">
              <div className="form-section">
                <h3 className="section-heading">User Information</h3>
                <div className="form-row">
                  <div className="form-group">
                    <label>Username *</label>
                    <input 
                      type="text" 
                      name="username" 
                      value={formData.username} 
                      onChange={handleChange}
                      onKeyDown={handleUsernameKeyDown}
                      onPaste={handleUsernamePaste}
                      placeholder="Enter username"
                      required 
                    />
                  </div>
                  <div className="form-group">
                    <label>Full Name *</label>
                    <input 
                      type="text" 
                      name="fullName" 
                      value={formData.fullName} 
                      onChange={handleChange}
                      onKeyDown={handleNameKeyDown}
                      onPaste={handleNamePaste}
                      placeholder="Enter full name"
                      required 
                    />
                  </div>
                </div>
                <div className="form-row">
                  <div className="form-group">
                    <label>Email *</label>
                    <input 
                      type="email" 
                      name="email" 
                      value={formData.email} 
                      onChange={handleChange} 
                      placeholder="email@example.com"
                      required 
                    />
                  </div>
                  <div className="form-group">
                    <label>Password *</label>
                    <div className="password-input-wrapper">
                      <input 
                        type={showPassword ? "text" : "password"}
                        name="password" 
                        value={formData.password} 
                        onChange={handleChange} 
                        placeholder="Enter password"
                        required 
                      />
                      <button
                        type="button"
                        className="password-toggle-btn"
                        onClick={() => setShowPassword(!showPassword)}
                        title={showPassword ? "Hide password" : "Show password"}
                      >
                        {showPassword ? (
                          <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                            <path d="M17.94 17.94A10.07 10.07 0 0 1 12 20c-7 0-11-8-11-8a18.45 18.45 0 0 1 5.06-5.94M9.9 4.24A9.12 9.12 0 0 1 12 4c7 0 11 8 11 8a18.5 18.5 0 0 1-2.16 3.19m-6.72-1.07a3 3 0 1 1-4.24-4.24"></path>
                            <line x1="1" y1="1" x2="23" y2="23"></line>
                          </svg>
                        ) : (
                          <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                            <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z"></path>
                            <circle cx="12" cy="12" r="3"></circle>
                          </svg>
                        )}
                      </button>
                    </div>
                  </div>
                </div>
                <div className="form-row">
                  <div className="form-group">
                    <label>Role *</label>
                    <select name="role" value={formData.role} onChange={handleChange} required>
                      <option value="Waff Clerk">Waff Clerk</option>
                      <option value="Office Executive">Office Executive</option>
                      <option value="Manager">Manager</option>
                      <option value="Admin">Admin</option>
                      <option value="Super Admin">Super Admin</option>
                    </select>
                  </div>
                </div>
              </div>
              
              <div className="form-actions">
                <button type="button" onClick={() => setShowModal(false)} className="btn btn-secondary">Cancel</button>
                <button type="submit" className="btn btn-primary">Create User</button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}

export default UserManagement;
